<script setup lang="ts">
import type { ISchoolGradeDisplayConfiguration } from '@/core/school-grades/schoolGrade.types';
import SchoolGradeBadge from '@/core/school-grades/SchoolGradeBadge.vue';
import type { Components } from '@/api/generated/api';
import NoteIcon from '@/assets/icons/NoteIcon.vue';
import PerformanceLevelVisualBadge from '@/core/badges/PerformanceLevelVisualBadge.vue';
import PerformanceLevelVisualBadgeGroup from '@/core/performance-visuals/PerformanceLevelVisualBadgeGroup.vue';
import UserAvatar from '@/core/UserAvatar.vue';

defineProps<{
  gradeConfiguration: ISchoolGradeDisplayConfiguration | null;
  competenceRatings: Components.Schemas.PerformanceLevelV2Dto[];
  fullyRated: boolean;
  hasPerformanceVisual: boolean;
  hasNote: boolean;
  name: string;
  gradeValue?: number | null;
  overallRating: Components.Schemas.PerformanceLevelV2Dto | null;
  selected: boolean;
  student: Components.Schemas.BaseStudentDto | Components.Schemas.StudentWithSubjectAccessDto;
}>();

const emit = defineEmits<{
  select: [];
}>();
</script>

<template>
  <button
    type="button"
    class="popover-option justify-center"
    :class="selected && 'bg-highlight'"
    :aria-pressed="selected"
    @click="emit('select')"
  >
    <UserAvatar :user="student" class="!size-8" />
    <span class="flex min-w-0 flex-1 items-center gap-sm">
      <span
        class="min-w-0 truncate text-body leading-[1.7]"
        :class="selected ? 'font-semibold text-tint' : 'font-normal text-primary'"
      >
        {{ name }}
      </span>
      <span v-if="!fullyRated" aria-hidden="true" class="size-2 shrink-0 rounded-full bg-surface-warning" />
      <span
        v-if="hasNote"
        class="flex size-3 shrink-0 text-secondary"
        role="img"
        :aria-label="$t('performanceAssessmentEntry.detailDialog.noteAvailable')"
      >
        <NoteIcon class="size-full" />
      </span>
    </span>

    <SchoolGradeBadge
      v-if="gradeValue != null"
      :configuration="gradeConfiguration"
      :value="gradeValue"
      variant="compact"
    />
    <PerformanceLevelVisualBadge
      v-else-if="hasPerformanceVisual && overallRating"
      class="!size-6 text-[6px] font-semibold leading-[1.4] tracking-[0.01em] text-tint"
      :performance-level="overallRating"
      :aria-label="overallRating.level.toString()"
      variant="compact"
    />

    <PerformanceLevelVisualBadgeGroup
      v-else-if="!hasPerformanceVisual && competenceRatings.length"
      class="shrink-0"
      :levels="competenceRatings"
    />
  </button>
</template>
